import moment from "moment";

/** @type {RegExp} Pre-compiled regex for quadrimester format (e.g. "2024-QM2") */
const QUADRIMESTER_REGEX = /^(\d{4})-QM([1-3])$/;

/** @type {RegExp} Pre-compiled regex for semester format (e.g. "2024-S1") */
const SEMESTER_REGEX = /^(\d{4})-S([1-2])$/;

/**
 * Parses a quadrimester string into a moment object.
 * QM1 = Jan-Apr, QM2 = May-Aug, QM3 = Sep-Dec
 *
 * @param {string} value - Quadrimester string (e.g. "2024-QM2")
 * @param {boolean} [endOfPeriod=false] - If true, returns the last instant of the period
 * @returns {moment.Moment|null} Parsed moment or null if the format doesn't match
 */
function parseQuadrimester(value, endOfPeriod = false) {
  const match = QUADRIMESTER_REGEX.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const startMonth = (Number(match[2]) - 1) * 4;

  return endOfPeriod ? moment({year, month: startMonth + 3}).endOf("month") : moment({year, month: startMonth, day: 1});
}

/**
 * Parses a semester string into a moment object.
 * S1 = Jan-Jun, S2 = Jul-Dec
 *
 * @param {string} value - Semester string (e.g. "2024-S1")
 * @param {boolean} [endOfPeriod=false] - If true, returns the last instant of the period
 * @returns {moment.Moment|null} Parsed moment or null if the format doesn't match
 */
function parseSemester(value, endOfPeriod = false) {
  const match = SEMESTER_REGEX.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const isFirst = Number(match[2]) === 1;

  return endOfPeriod
    ? moment({year, month: isFirst ? 5 : 11}).endOf("month")
    : moment({year, month: isFirst ? 0 : 6, day: 1});
}

/**
 * Parses a date string with support for custom period formats:
 *  - Quarters: "YYYY-Qn" (native moment)
 *  - Semesters: "YYYY-Sn"
 *  - Quadrimesters: "YYYY-QMn"
 *
 * @param {string} value - Date or period string to parse
 * @param {boolean} [endOfPeriod=false] - If true, returns the last instant of the period
 * @returns {moment.Moment} Parsed moment (falls back to standard moment parsing)
 */
moment.fn.customParse = function (value, endOfPeriod) {
  // Try standard quarter format first (most common)
  const quarter = moment(value, "YYYY-[Q]Q", true);
  if (quarter.isValid()) {
    return endOfPeriod ? quarter.endOf("quarter") : quarter;
  }

  // Try custom period formats
  const semester = parseSemester(value, endOfPeriod);
  if (semester) return semester;

  const quadrimester = parseQuadrimester(value, endOfPeriod);
  if (quadrimester) return quadrimester;

  // Year-only string: "YYYY"
  if (/^\d{4}$/.test(value)) {
    const m = moment(value, "YYYY");
    return endOfPeriod ? m.endOf("year") : m;
  }

  // Month string: "YYYY-MM"
  if (/^\d{4}-\d{2}$/.test(value)) {
    const m = moment(value, "YYYY-MM");
    return endOfPeriod ? m.endOf("month") : m;
  }

  // Falls back to standard moment
  return moment(value);
};

/**
 * Formats a moment object using a custom format string.
 * Supports special tokens:
 *  - "Sm" → semester number (1 or 2), with "[S]" rendered as literal "S"
 *  - "Qm" → quadrimester number (1-3), with "[QM]" rendered as literal "QM"
 *
 * Falls back to standard moment formatting for other format strings.
 *
 * @param {string} stringFormat - Format string with optional custom tokens
 * @returns {string} Formatted date string
 */
moment.fn.customFormat = function (stringFormat) {
  const year = this.year();
  const month = this.month(); // 0-based

  if (stringFormat && stringFormat.includes("Sm")) {
    // Semester: derive from 0-based month
    const semester = month < 6 ? 1 : 2;
    return stringFormat.replace("[S]", "S").replace("Sm", semester).replace("YYYY", year);
  }

  if (stringFormat && stringFormat.includes("Qm")) {
    // Quadrimester: 4-month periods (Jan-Apr=1, May-Aug=2, Sep-Dec=3)
    const quadrimester = Math.floor(month / 4) + 1;
    return stringFormat.replace("[QM]", "QM").replace("Qm", quadrimester).replace("YYYY", year);
  }

  return this.format(stringFormat);
};

/**
 * Returns an ISO 8601 datetime string without timezone offset.
 *
 * @returns {string} Formatted string (e.g. "2024-06-15T14:30:00")
 */
moment.fn.toISOStringNoOffset = function () {
  return this.format("YYYY-MM-DDTHH:mm:ss");
};
